import type { TNTDataSetVersionDto } from '@shipium-corp/shipium-ts-types';
import type { Action, Computed, Thunk } from 'easy-peasy';
import { action, computed, thunk } from 'easy-peasy';
import type { StoreModel } from '../';
import type { ApplicationErrorOptions } from '../../types';
import parseStatusCode from '../errors/parseStatusCode';
import { downloadCsvFile } from '../files/fileUtils';
import type { Injections } from '../services/injections';
import type {
  CreateDataSet,
  CreateDataSetResponse,
  DataSetResponseV2,
  DownloadTntRecordsRequestV2,
  DownloadTntResponse,
  EditDataSet,
  GetTntStatusResponse,
  TntDataSet,
  TntDataSetsV2,
  TntDataSetType,
  TntDataSetTypesSearchBody,
  TntDataSetV2,
  TntJobsV2,
  TntJobV2,
  TntRecordsResponse,
  JsonObject,
  TntJob,
  TntSource,
  UpdateDataSetTypeFromUploadRequestV2,
  UploadDataSet,
  UploadProcessTntRecordsRequest,
  UploadProcessTntRecordsRequestV2,
  UploadProdTntResponse,
  UploadTntResponse,
} from '../services/TimeInTransitService/types';
import type {
  TimeInTransitTableData,
  TimeInTransitState,
  TimeInTransitStateMap,
  PaginatedTntData,
  PaginatedTntTableData,
} from './types';
import objecthash from 'object-hash';
// Single source of truth for the promise tnt-source/file-tnt-source literals (see the comment on
// TNT_UPLOAD_TYPE_CONFIG.promise) — referenced here instead of re-hardcoding the strings.
import { TNT_UPLOAD_TYPE_CONFIG } from '../../components/timeInTransit/tntUploadTypeConfig';

export interface TimeInTransitModel {
  lastAction?: number;
  timeInTransitActionStart: Action<TimeInTransitModel, void>;
  timeInTransitState: TimeInTransitState;
  tntJobs: TntJobsV2;
  simulationJobs: TntJobsV2;
  carrierDefaultJobs: TntJobsV2;
  // Own slot for the promise tab's jobs — fetchTntJobsV2 is server-scoped to a single tntDataSetId
  // and setTntJobs replaces `tntJobs` wholesale, so the prod and promise tabs cannot safely share
  // that slot. Mirrors carrierDefaultJobs.
  promiseJobs: TntJobsV2;
  tntJobById: TntJobV2;
  tntRecordsByVersion: PaginatedTntTableData[];
  prodDataSetType: TimeInTransitStateMap;
  prodDataSets: TntDataSetsV2;
  simulationDataSetType: TimeInTransitStateMap;
  dataSetsV2: DataSetResponseV2[];
  tntSources: Record<string, TntSource>;
  downloadingTnt: Record<string, boolean>;
  timeInTransitSimulationTableData: Computed<TimeInTransitModel, TimeInTransitTableData[]>;
  timeInTransitProductionTableData: Computed<TimeInTransitModel, TimeInTransitTableData[]>;
  tntProdTableDataWithName: Computed<TimeInTransitModel, { jobs: TntJobV2[] }>;
  tntPromiseTableDataWithName: Computed<TimeInTransitModel, { jobs: TntJobV2[] }>;
  tntSimulationTableDataWithName: Computed<TimeInTransitModel, (partnerId: string) => TntJobV2[]>;
  tntCarrierDefaultTableDataWithName: Computed<TimeInTransitModel, TntJobV2[]>;
  setTntJobs: Action<TimeInTransitModel, TntJobsV2>;
  setSimulationJobs: Action<TimeInTransitModel, TntJobsV2>;
  setCarrierDefaultJobs: Action<TimeInTransitModel, TntJobsV2>;
  setPromiseJobs: Action<TimeInTransitModel, TntJobsV2>;
  setTntJobById: Action<TimeInTransitModel, TntJobV2>;
  setDataSetsV2: Action<TimeInTransitModel, DataSetResponseV2[]>;
  setTimeInTransitState: Action<TimeInTransitModel, TimeInTransitState>;
  setProdDataSetType: Action<TimeInTransitModel, TimeInTransitStateMap>;
  setProdDataSets: Action<TimeInTransitModel, TntDataSetsV2>;
  setSimulationDataSetType: Action<TimeInTransitModel, TimeInTransitStateMap>;
  setTntSources: Action<TimeInTransitModel, TntSource[]>;
  setTntRecordsByVersion: Action<TimeInTransitModel, PaginatedTntTableData[]>;
  setDownloadingTnt: Action<TimeInTransitModel, { tntSearch: DownloadTntRecordsRequestV2; loading: boolean }>;
  timeInTransitActionFailure: Action<TimeInTransitModel, ApplicationErrorOptions>;
  fetchTntDataSetTypes: Thunk<
    TimeInTransitModel,
    TntDataSetTypesSearchBody,
    Injections,
    StoreModel,
    Promise<TntDataSetType[]>
  >;
  fetchTntDataSets: Thunk<
    TimeInTransitModel,
    { dataSetTypesRes: TntDataSetType[]; tntSources: TntSource[]; tntJobs?: TntJob[] },
    Injections,
    StoreModel,
    Promise<TntDataSet[]>
  >;
  fetchProdTntDataSet: Thunk<TimeInTransitModel, string, Injections, StoreModel, Promise<TntDataSet[]>>;
  fetchTntSources: Thunk<TimeInTransitModel, unknown, Injections, StoreModel, Promise<TntSource[]>>;
  fetchTntRecords: Thunk<TimeInTransitModel, JsonObject, Injections, StoreModel, Promise<TntRecordsResponse[]>>;
  fetchTntRecordsByVersion: Thunk<
    TimeInTransitModel,
    { tntDataSetVersionId: string } & JsonObject,
    Injections,
    StoreModel,
    Promise<PaginatedTntData[]>
  >;
  updateDataSetType: Thunk<TimeInTransitModel, TntDataSetType, Injections, StoreModel, Promise<TntRecordsResponse[]>>;
  deleteDataSetType: Thunk<TimeInTransitModel, any, Injections, StoreModel, Promise<TntRecordsResponse[]>>;
  downloadTntRecordsBySearch: Thunk<
    TimeInTransitModel,
    { name: string; tntSearch: DownloadTntRecordsRequestV2 },
    Injections,
    StoreModel,
    Promise<DownloadTntResponse>
  >;
  downloadTntRecordsBySearchV2: Thunk<
    TimeInTransitModel,
    { name: string; tntSearch: DownloadTntRecordsRequestV2 },
    Injections,
    StoreModel,
    Promise<DownloadTntResponse>
  >;
  createAsyncDataSetTypeFromUpload: Thunk<
    TimeInTransitModel,
    UploadProcessTntRecordsRequest,
    Injections,
    StoreModel,
    Promise<UploadTntResponse>
  >;
  createAsyncDataSetTypeFromUploadV2: Thunk<
    TimeInTransitModel,
    UploadProcessTntRecordsRequestV2,
    Injections,
    StoreModel,
    Promise<UploadProdTntResponse>
  >;
  updateDataSetTypeFromUploadV2: Thunk<
    TimeInTransitModel,
    UpdateDataSetTypeFromUploadRequestV2,
    Injections,
    StoreModel,
    Promise<UploadProdTntResponse>
  >;
  createDataSet: Thunk<TimeInTransitModel, CreateDataSet, Injections, StoreModel, Promise<CreateDataSetResponse>>;
  uploadDataSet: Thunk<TimeInTransitModel, UploadDataSet, Injections, StoreModel, Promise<TntRecordsResponse>>;
  deleteDataSet: Thunk<TimeInTransitModel, string, Injections, StoreModel, Promise<TntRecordsResponse>>;
  writeDataSet: Thunk<TimeInTransitModel, string, Injections, StoreModel, Promise<TntRecordsResponse>>;
  editDataSet: Thunk<TimeInTransitModel, EditDataSet, Injections, StoreModel, Promise<TntRecordsResponse>>;
  activateTnt: Thunk<TimeInTransitModel, string, Injections, StoreModel, Promise<TntRecordsResponse>>;
  deactivateTnt: Thunk<TimeInTransitModel, string, Injections, StoreModel, Promise<TntRecordsResponse>>;
  getTntStatus: Thunk<TimeInTransitModel, string, Injections, StoreModel, Promise<GetTntStatusResponse>>;
  fetchTntJobByIdV2: Thunk<TimeInTransitModel, string, Injections, StoreModel, Promise<TntDataSetV2[]>>;
  fetchTntJobsV2: Thunk<TimeInTransitModel, string | undefined, Injections, StoreModel, Promise<TntDataSetV2[]>>;
  fetchSimulationTntJobsV2: Thunk<TimeInTransitModel, void, Injections, StoreModel, Promise<TntDataSetV2[]>>;
  fetchCarrierDefaultTntJobsV2: Thunk<TimeInTransitModel, unknown, Injections, StoreModel, Promise<TntDataSetV2[]>>;
  // Scoped the same way as fetchTntJobsV2 (by tntDataSetId), but writes into its own `promiseJobs`
  // slot so it can never clobber (or be clobbered by) the production tab's fetch.
  fetchPromiseTntJobsV2: Thunk<TimeInTransitModel, string | undefined, Injections, StoreModel, Promise<TntDataSetV2[]>>;
}

export const hashTNTSearch = (serachObj: DownloadTntRecordsRequestV2) => {
  return objecthash(serachObj);
};

export const timeInTransitModel: TimeInTransitModel = {
  timeInTransitState: {},
  prodDataSetType: {} as TimeInTransitStateMap,
  prodDataSets: {} as TntDataSetsV2,
  simulationDataSetType: {} as TimeInTransitStateMap,
  dataSetsV2: [],
  tntJobs: { jobs: [] } as TntJobsV2,
  simulationJobs: { jobs: [] } as TntJobsV2,
  carrierDefaultJobs: { jobs: [] } as TntJobsV2,
  promiseJobs: { jobs: [] } as TntJobsV2,
  tntRecordsByVersion: [],
  tntJobById: {} as TntJobV2,
  tntSources: {},
  downloadingTnt: {},
  timeInTransitActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  setDownloadingTnt: action((state, payload) => {
    const hash = hashTNTSearch(payload.tntSearch);
    state.downloadingTnt[hash] = payload.loading;
  }),
  timeInTransitSimulationTableData: computed((state) => {
    return Object.values(state.timeInTransitState)
      .filter((tntData) => tntData.usage === 'SIMULATION')
      .map((tntData) => {
        const tableData: TimeInTransitTableData = {
          dataSetTypeId: tntData.id,
          dataSetId: tntData.dataSets[0]?.id,
          tntSourceId: tntData.tntSource?.id,
          name: tntData.name,
          description: tntData.description,
          tntSource: tntData.tntSource.name,
          createdBy: tntData.auditInfo?.createdBy,
          writeCompletedAt: tntData.dataSets[0]?.writeCompletedAt || tntData.auditInfo.createdAt,
          expirationTimestamp: tntData.expirationTimestamp,
          usage: tntData.usage,
          tntJobs: tntData.tntJobs,
        };
        return tableData;
      });
  }),
  timeInTransitProductionTableData: computed((state) => {
    return Object.values(state.timeInTransitState)
      .filter((tntData) => tntData.usage === 'PROD')
      .map((tntData) => {
        const tableData: TimeInTransitTableData = {
          dataSetTypeId: tntData.id,
          dataSetId: tntData.dataSets[0]?.id,
          tntSourceId: tntData.tntSource?.id,
          name: tntData.name,
          description: tntData.description,
          tntSource: tntData.tntSource.name,
          createdBy: tntData.auditInfo?.createdBy,
          writeCompletedAt: tntData.dataSets[0]?.writeCompletedAt || tntData.auditInfo.createdAt,
          expirationTimestamp: tntData.expirationTimestamp,
          usage: tntData.usage,
          tntJobs: tntData.tntJobs,
        };
        return tableData;
      });
  }),
  tntProdTableDataWithName: computed((state) => {
    const namedProdData: TntJobV2[] = Object.values(state.tntJobs.jobs)
      .filter((job) => job.fileTntSource === 'PARTNER_PROVIDED_POSTAL' && job.usage === 'PROD')
      .map((tntJob) => {
        const currentDataSet = state.prodDataSetType?.dataSets.filter(
          (dataSet) => dataSet.id === tntJob.tntDataSetVersionId,
        )?.[0];
        const tabledata: TntJobV2 = {
          tntDataSetDescription: currentDataSet?.description || '',
          tntDataSetName: currentDataSet?.dataSetName || '',
          versionNumber: currentDataSet?.versionNumber,
          ...tntJob,
        };
        return tabledata;
      });
    return { jobs: namedProdData };
  }),
  tntPromiseTableDataWithName: computed((state) => {
    // Mirrors tntProdTableDataWithName exactly, but sourced from the promise tab's own `promiseJobs`
    // slot (never `tntJobs`) and keyed to the promise tnt-source/file-tnt-source instead of the
    // hardcoded production ones.
    const promiseDataSetType = Object.values(state.timeInTransitState).find(
      (entry) => entry.usage === 'PROD' && entry?.tntSource?.name === TNT_UPLOAD_TYPE_CONFIG.promise.tntSourceName,
    );
    const namedPromiseData: TntJobV2[] = Object.values(state.promiseJobs.jobs)
      .filter((job) => job.fileTntSource === TNT_UPLOAD_TYPE_CONFIG.promise.fileTntSource && job.usage === 'PROD')
      .map((tntJob) => {
        const currentDataSet = promiseDataSetType?.dataSets?.filter(
          (dataSet) => dataSet.id === tntJob.tntDataSetVersionId,
        )?.[0];
        const tabledata: TntJobV2 = {
          tntDataSetDescription: currentDataSet?.description || '',
          tntDataSetName: currentDataSet?.dataSetName || '',
          versionNumber: currentDataSet?.versionNumber,
          ...tntJob,
        };
        return tabledata;
      });
    return { jobs: namedPromiseData };
  }),
  tntSimulationTableDataWithName: computed((state) => {
    return (partnerId: string) =>
      state.dataSetsV2
        .filter((val) => val.usage === 'SIMULATION' && val.scope.partnerId === partnerId)
        .map((val) => {
          // there should only be one job per tnt data set going forward
          // this is to do out best with existing stuff
          const allJobs = state.simulationJobs.jobs.filter((job) => job.tntDataSetId === val.id);
          const job = allJobs?.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())?.[
            allJobs.length - 1
          ];
          return {
            tntDataSetDescription: val.description,
            tntDataSetName: val.name,
            uploadJobId: job?.uploadJobId,
            tntDataSetId: val.id,
            createdBy: val.auditInfo.createdBy,
            createdAt: job?.createdAt,
            versionNumber: 1,
            uploadJobStatus: job?.uploadJobStatus,
            // Deliberately a partial row: the simulation tab's table only reads these fields.
            // Typed as TntJobV2 because that is what the shared table accepts.
          } as TntJobV2;
        });
  }),

  tntCarrierDefaultTableDataWithName: computed((state) => {
    const namedCarrierDefaultData: TntJobV2[] = Object.values(state.carrierDefaultJobs.jobs)
      .filter((job) => job.fileTntSource === 'CARRIER_DEFAULT')
      .map((tntJob) => {
        const currentDataSeVersion = state.dataSetsV2
          .filter((dataSet) => dataSet.id === tntJob.tntDataSetId)?.[0]
          ?.dataSetVersions?.filter((dataSetVersion) => dataSetVersion.id === tntJob.tntDataSetVersionId)?.[0];
        const tabledata: TntJobV2 = {
          tntDataSetDescription: currentDataSeVersion?.description || '',
          tntDataSetName: currentDataSeVersion?.name || '',
          versionNumber: currentDataSeVersion?.versionNumber,
          carrierId:
            state.dataSetsV2.filter((dataSet) => dataSet.id === tntJob.tntDataSetId)?.[0]?.scope.carrierId || '',
          ...tntJob,
        };
        return tabledata;
      });
    return namedCarrierDefaultData;
  }),

  setTimeInTransitState: action((state, payload) => {
    state.timeInTransitState = payload;
  }),
  setTntJobs: action((state, payload) => {
    state.tntJobs = { jobs: payload.jobs };
  }),
  setSimulationJobs: action((state, payload) => {
    state.simulationJobs = { jobs: payload.jobs };
  }),
  setCarrierDefaultJobs: action((state, payload) => {
    state.carrierDefaultJobs = { jobs: payload.jobs };
  }),
  setPromiseJobs: action((state, payload) => {
    state.promiseJobs = { jobs: payload.jobs };
  }),
  setTntJobById: action((state, payload) => {
    state.tntJobById = payload;
  }),
  setTntRecordsByVersion: action((state, payload) => {
    state.tntRecordsByVersion = payload;
  }),
  setDataSetsV2: action((state, payload) => {
    state.dataSetsV2 = payload;
  }),
  setProdDataSetType: action((state, payload) => {
    state.prodDataSetType = payload;
  }),
  setProdDataSets: action((state, payload) => {
    state.prodDataSets = payload;
  }),
  setSimulationDataSetType: action((state, payload) => {
    state.simulationDataSetType = payload;
  }),
  setTntSources: action((state, payload) => {
    const tntSources = payload.reduce((prev: Record<string, TntSource>, curr: TntSource) => {
      prev[curr.id] = curr;
      return prev;
    }, {});
    state.tntSources = tntSources;
  }),
  timeInTransitActionFailure: action((state, payload) => {
    console.log(payload);
  }),
  fetchTntDataSetTypes: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const body: TntDataSetTypesSearchBody = payload;
      actions.timeInTransitActionStart();
      const [dataSetTypesRes, tntSources, tntJobs] = await Promise.all([
        await timeInTransitService.fetchTntDataSetTypes(body),
        await timeInTransitService.fetchTntSources(),
        await timeInTransitService.fetchTntJobs(),
      ]);
      actions.fetchTntDataSets({ dataSetTypesRes, tntSources, tntJobs });
      return dataSetTypesRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchTntDataSets: thunk(async (actions, payload, { injections }) => {
    try {
      const dataSetTypes: TntDataSetType[] = payload.dataSetTypesRes;
      const tntSources: TntSource[] = payload.tntSources;
      // const tntJobs: TntJob[] = payload.tntJobs;
      const { timeInTransitService } = injections;
      const tntState = {} as TimeInTransitState;
      actions.timeInTransitActionStart();

      // TODO to be used after tntjobs endpoint update
      // const tntJobsByDataSetTypeId: Record<string, TntJob[]> = tntJobs.reduce((prev, curr) => {
      //   if (!prev[curr.dataSetTypeId]) prev[curr.dataSetTypeId] = [];

      //   prev[curr.dataSetTypeId].push(curr)
      //   return prev;
      // }, {} as Record<string, TntJob[]>)
      const dataSetsV2 = await timeInTransitService.fetchDataSetsV2();

      const dataSetMap = dataSetsV2.reduce(
        (prev, curr) => {
          prev[curr.id] = curr;
          return prev;
        },
        {} as { [key: string]: DataSetResponseV2 },
      );
      const allDataSetVersions = await timeInTransitService.fetchDataSetVersionsBulk(
        dataSetsV2.map((dataSet) => dataSet.id),
      );
      Object.entries(allDataSetVersions).forEach(([id, dataSetVersionsArray]) => {
        dataSetMap[id] = {
          ...(dataSetMap[id] ?? {}),
          // fetchDataSetVersionsBulk is declared to return DataSetVersionsResponse[] (wrapper
          // objects with a nested dataSetVersions array), but tntCarrierDefaultTableDataWithName
          // reads .id/.name/.versionNumber straight off these elements. One of the two is wrong —
          // if the declared type is right, that lookup never matches and the carrier-default table
          // silently shows blank name/description. The cast follows the runtime shape.
          dataSetVersions: (dataSetVersionsArray || []) as unknown as TNTDataSetVersionDto[],
        };
      });
      actions.setDataSetsV2(Object.values(dataSetMap));

      // Fetch data sets and jobs in bulk
      const dataSetTypeIds = dataSetTypes.map((dt) => dt.id);

      const [bulkDataSets, bulkJobs] = await Promise.all([
        timeInTransitService.fetchTntDataSetsBulk(dataSetTypeIds),
        timeInTransitService.fetchTntJobsBulk(dataSetTypeIds),
      ]);
      // Process each dataSetType with the bulk fetched data
      dataSetTypes.forEach((dataSetType) => {
        const dataSets = bulkDataSets[dataSetType.id] || [];
        const tntJobs = bulkJobs[dataSetType.id] || [];
        const tntStateVal = {
          ...dataSetType,
          dataSets: dataSets.filter((dataSet) => dataSet.deletedAt === null),
          tntSource: tntSources.find((t) => t.id === dataSetType.tntSourceId) ?? ({} as any),
          tntJobs: tntJobs,
        };
        tntState[dataSetType.id] = tntStateVal;

        if (
          dataSetType.usage === 'PROD' &&
          dataSetType.scope.partnerId &&
          dataSetType.tntSourceId === tntSources.find((tntSource) => tntSource.name === 'partner-provided-postal')?.id
        ) {
          actions.setProdDataSetType(tntStateVal);
        }
        if (dataSetType.usage === 'SIMULATION' && dataSetType.scope.partnerId) {
          actions.setSimulationDataSetType(tntStateVal);
        }
      });

      actions.setTimeInTransitState(tntState);
      actions.setTntSources(tntSources);
      return tntState;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchTntSources: thunk(async (actions, _payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      actions.timeInTransitActionStart();
      const tntSources = await timeInTransitService.fetchTntSources();
      actions.setTntSources(tntSources);
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchTntRecords: undefined as any,
  fetchTntRecordsByVersion: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      actions.timeInTransitActionStart();
      const tntRecords = await timeInTransitService.fetchTntRecordsByVersion(payload.tntDataSetVersionId, {
        ...payload,
      });
      const tableData = tntRecords.map((record) => {
        return {
          originCountry: record.route.originDestination.origin.countryCode,
          originPostal: record.route.originDestination.origin.postalCode,
          destinationCountry: record.route.originDestination.destination.countryCode,
          destinationPostal: record.route.originDestination.destination.postalCode,
          carrierServiceMethodId: record.carrierScope.id,
          tnt: record?.tntDimensionsByTime?.['*']?.tntByDimension?.OVERRIDE,
        };
      });
      actions.setTntRecordsByVersion(tableData);
      return tableData;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  downloadTntRecordsBySearch: thunk(async (actions, payload, { injections }) => {
    try {
      const tntSearch: DownloadTntRecordsRequestV2 = payload.tntSearch;
      const name: string = payload.name;
      const { timeInTransitService } = injections;

      actions.setDownloadingTnt({ tntSearch, loading: true });

      const csvString = await timeInTransitService.downloadTntRecordsBySearch(tntSearch);
      const fileDateWithSeconds = new Date().toISOString().split('.')[0].replace(/:/g, '').replace('T', '-');
      const filename = `${name}-time-in-transit-${fileDateWithSeconds}`;

      downloadCsvFile(csvString, filename);

      actions.setDownloadingTnt({ tntSearch, loading: false });

      return;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),

  downloadTntRecordsBySearchV2: thunk(async (actions, payload, { injections }) => {
    const tntSearch: DownloadTntRecordsRequestV2 = payload.tntSearch;
    try {
      const name: string = payload.name;
      const { timeInTransitService } = injections;

      actions.setDownloadingTnt({ tntSearch, loading: true });

      const csvString = await timeInTransitService.downloadTntRecordsBySearchV2(tntSearch);
      const fileDateWithSeconds = new Date().toISOString().split('.')[0].replace(/:/g, '').replace('T', '-');
      const filename = `${name}-time-in-transit-${fileDateWithSeconds}`;

      downloadCsvFile(csvString, filename);

      return csvString;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    } finally {
      // Must be in `finally`: clearing this only on the success path left the button spinning
      // forever whenever the download failed, hiding the error behind an endless spinner.
      actions.setDownloadingTnt({ tntSearch, loading: false });
    }
  }),
  updateDataSetType: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;

      // The caller supplies the complete record with its edits already applied, so it is sent
      // as-is: the PUT needs every field, not just the edited ones.
      await timeInTransitService.updateDataSetType(payload);

      return;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),

  deleteDataSetType: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      await timeInTransitService.deleteDataSetType(payload);
      return;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchProdTntDataSet: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const dataSetRes = await timeInTransitService.fetchTntDataSets(payload, true);
      return dataSetRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  createAsyncDataSetTypeFromUpload: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      actions.timeInTransitActionStart();
      const uploadRes = await timeInTransitService.createAsyncDataSetTypeFromUpload(payload);

      actions.fetchTntDataSetTypes({});

      return uploadRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  createAsyncDataSetTypeFromUploadV2: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      actions.timeInTransitActionStart();
      const uploadRes = await timeInTransitService.createAsyncDataSetTypeFromUploadV2(payload);
      if (payload.usage === 'SIMULATION') {
        actions.fetchSimulationTntJobsV2();
      }
      actions.fetchTntDataSetTypes({});
      return uploadRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  updateDataSetTypeFromUploadV2: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      actions.timeInTransitActionStart();
      const uploadRes = await timeInTransitService.updateDataSetTypeFromUploadV2(payload.tntDataSetId, { ...payload });

      actions.fetchTntDataSetTypes({});

      return uploadRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  createDataSet: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const createDataSet = await timeInTransitService.createDataSet(payload);

      actions.fetchTntDataSetTypes({});

      return createDataSet;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  uploadDataSet: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const uploadDataSet = await timeInTransitService.uploadDataSet(payload.dataSetTypeId, { ...payload });

      return uploadDataSet;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  deleteDataSet: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const createDataSet = await timeInTransitService.deleteDataSet(payload);

      actions.fetchTntDataSetTypes({});

      return createDataSet;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  writeDataSet: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const createDataSet = await timeInTransitService.writeDataSet(payload);

      actions.fetchTntDataSetTypes({});

      return createDataSet;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  editDataSet: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;

      const editDataSet = await timeInTransitService.editDataSet(payload);
      actions.fetchTntDataSetTypes({});

      return editDataSet;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  activateTnt: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const activateTnt = await timeInTransitService.activateTnt(payload);

      actions.fetchTntDataSetTypes({});

      return activateTnt;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  deactivateTnt: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const deactivateTnt = await timeInTransitService.deactivateTnt(payload);

      actions.fetchTntDataSetTypes({});

      return deactivateTnt;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  getTntStatus: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const tntStatus = await timeInTransitService.getTntStatus(payload);

      return tntStatus;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchTntJobByIdV2: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const jobRes = await timeInTransitService.fetchTntJobByIdV2(payload);
      actions.setTntJobById(jobRes);
      return jobRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchTntJobsV2: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const jobRes = await timeInTransitService.fetchTntJobsV2(payload);
      actions.setTntJobs({ jobs: jobRes });
      return jobRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchSimulationTntJobsV2: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const jobRes = await timeInTransitService.fetchTntJobsV2();
      actions.setSimulationJobs({ jobs: jobRes.filter((res) => res.usage === 'SIMULATION') });
      return jobRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchCarrierDefaultTntJobsV2: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const jobRes = await timeInTransitService.fetchTntJobsV2();
      actions.setCarrierDefaultJobs({ jobs: jobRes });
      return jobRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchPromiseTntJobsV2: thunk(async (actions, payload, { injections }) => {
    try {
      const { timeInTransitService } = injections;
      const jobRes = await timeInTransitService.fetchTntJobsV2(payload);
      actions.setPromiseJobs({ jobs: jobRes });
      return jobRes;
    } catch (error) {
      actions.timeInTransitActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
};
